import { useEffect, useState, useCallback, useMemo } from "react";
import { Combobox, Listbox, Icon, LegacyStack, Tag } from "@shopify/polaris";
import { SearchIcon } from "@shopify/polaris-icons";
import { useTranslation } from "react-i18next";
import { fetchTabUsers } from "services/signontab.service";
import { useParams } from "react-router-dom";

interface TabUser {
  tabId: string;
  name: string;
}

interface SignontabSelectProps {
  value: string;
  onChange: (value: string) => void;
}

const SignontabSelect: React.FC<SignontabSelectProps> = ({
  value,
  onChange,
}) => {
  const { t } = useTranslation();
  const { companyId } = useParams();
  const [tabUsers, setTabUsers] = useState<TabUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [selectedOptions, setSelectedOptions] = useState<
    { label: string; value: string }[]
  >([]);

  const allOptions = useMemo(
    () =>
      tabUsers.map((user) => ({
        label: user.name,
        value: user.tabId,
      })),
    [tabUsers]
  );

  const displayOptions = useMemo(() => {
    const q = inputValue.trim().toLowerCase();
    if (!q) return allOptions;
    return allOptions.filter((o) => o.label.toLowerCase().includes(q));
  }, [allOptions, inputValue]);

  useEffect(() => {
    const loadUsers = async () => {
      if (!companyId) return;
      setIsLoading(true);
      try {
        const data = await fetchTabUsers(companyId);
        setTabUsers(Array.isArray(data) ? data : []);
      } catch (err) {
        setTabUsers([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadUsers();
  }, [companyId]);

  useEffect(() => {
    if (tabUsers.length > 0 && !value) {
      onChange(tabUsers[0].tabId);
    }
  }, [tabUsers, value, onChange]);

  useEffect(() => {
    if (!value) {
      setSelectedOptions([]);
      return;
    }
    const found = allOptions.find((o) => o.value === value);
    if (found) {
      setSelectedOptions([found]);
    }
  }, [value, allOptions]);

  const handleInputChange = useCallback((input: string) => {
    setInputValue(input);
  }, []);

  const handleSelection = useCallback(
    (selected: string) => {
      const found = displayOptions.find((o) => o.value === selected);
      if (found) {
        setSelectedOptions([found]);
        setInputValue("");
        onChange(selected);
      }
    },
    [displayOptions, onChange]
  );

  const handleTagRemove = useCallback(() => {
    setSelectedOptions([]);
    setInputValue("");
    onChange("");
  }, [onChange]);

  const optionsMarkup = displayOptions.map((option) => (
    <Listbox.Option
      key={option.value}
      value={option.value}
      selected={selectedOptions.some((o) => o.value === option.value)}
      accessibilityLabel={option.label}
    >
      {option.label}
    </Listbox.Option>
  ));

  const verticalContentMarkup =
    selectedOptions.length > 0 ? (
      <LegacyStack spacing="extraTight" alignment="center">
        <Tag onRemove={handleTagRemove}>{selectedOptions[0].label}</Tag>
      </LegacyStack>
    ) : null;

  return (
    <Combobox
      allowMultiple={false}
      activator={
        <Combobox.TextField
          prefix={<Icon source={SearchIcon as any} tone="base" />}
          onChange={handleInputChange}
          label={t("signontab_select_tablet_user")}
          value={inputValue}
          placeholder={
            t("user_search") !== "user_search"
              ? t("user_search")
              : "Search user..."
          }
          autoComplete="off"
          disabled={!isLoading && allOptions.length === 0}
          verticalContent={verticalContentMarkup}
        />
      }
    >
      <Listbox onSelect={handleSelection}>
        {isLoading ? (
          <Listbox.Loading accessibilityLabel="Loading" />
        ) : (
          <>{optionsMarkup}</>
        )}
      </Listbox>
    </Combobox>
  );
};

export default SignontabSelect;
